import { useCallback, useEffect, useRef, useState } from 'react';

import backgroundMusic from 'assets/audio/rewind/background-music.mp3';

type UseRewindMusicOptions = {
  /** Whether music should be enabled (default: true) */
  readonly enabled?: boolean;
  /** Volume 0-1 (default: 0.15) */
  readonly volume?: number;
};

type UseRewindMusicReturn = {
  /** Whether music is loaded and ready */
  readonly isReady: boolean;
  /** Whether background music is currently playing */
  readonly isMusicPlaying: boolean;
  /** Whether music is currently muted */
  readonly isMuted: boolean;
  /** Start playing background music */
  readonly playMusic: () => void;
  /** Pause music (keeps position) */
  readonly pauseMusic: () => void;
  /** Resume music from current position */
  readonly resumeMusic: () => void;
  /** Stop music */
  readonly stopMusic: () => void;
  /** Fade music volume */
  readonly fadeMusic: (targetVolume: number, durationMs?: number) => void;
  /** Toggle mute state */
  readonly toggleMute: () => void;
  /** Seek to a specific time in the background music (in milliseconds) */
  readonly seekToTime: (timeMs: number) => void;
  /** Get current playback time in milliseconds */
  readonly getCurrentTime: () => number;
};

/** Hook to manage background music for the Rewind modal. */
export function useRewindMusic(
  options: UseRewindMusicOptions = {},
): UseRewindMusicReturn {
  const { enabled = true, volume = 0.15 } = options;

  const [isMuted, setIsMuted] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);

  const musicRef = useRef<HTMLAudioElement | null>(null);
  const fadeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const effectiveVolume = isMuted ? 0 : volume;

  useEffect(() => {
    if (!enabled) return;

    const musicElement = new Audio();
    musicElement.loop = true;
    musicElement.volume = effectiveVolume;
    musicElement.preload = 'auto';
    // Preload audio immediately to avoid delay when playMusic() is called
    musicElement.src = backgroundMusic;
    musicRef.current = musicElement;

    setIsReady(true);

    return () => {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.src = '';
        musicRef.current = null;
      }
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Update volume when mute or volume settings change
  useEffect(() => {
    if (musicRef.current) {
      musicRef.current.volume = effectiveVolume;
    }
  }, [effectiveVolume]);

  const playMusic = useCallback(() => {
    if (!enabled || !musicRef.current) return;

    if (isMusicPlaying && !musicRef.current.paused) {
      return;
    }

    // Audio src is already set on mount for preloading
    musicRef.current.volume = effectiveVolume;
    musicRef.current
      .play()
      .then(() => {
        setIsMusicPlaying(true);
      })
      .catch((err) => {
        console.warn('Music autoplay blocked:', err);
      });
  }, [enabled, isMusicPlaying, effectiveVolume]);

  const stopMusic = useCallback(() => {
    if (musicRef.current) {
      musicRef.current.pause();
      musicRef.current.currentTime = 0;
    }
    setIsMusicPlaying(false);
  }, []);

  const pauseMusic = useCallback(() => {
    if (musicRef.current && !musicRef.current.paused) {
      musicRef.current.pause();
    }
  }, []);

  const resumeMusic = useCallback(() => {
    if (musicRef.current && musicRef.current.paused && isMusicPlaying) {
      musicRef.current.play().catch((err) => {
        console.warn('Music resume blocked:', err);
      });
    }
  }, [isMusicPlaying]);

  const fadeMusic = useCallback(
    (targetVolume: number, durationMs: number = 1000) => {
      if (!musicRef.current) return;

      const startVolume = musicRef.current.volume;
      const volumeDiff = targetVolume - startVolume;
      const steps = durationMs / 50;
      const volumeStep = volumeDiff / steps;
      let currentStep = 0;

      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
      }

      fadeIntervalRef.current = setInterval(() => {
        currentStep++;
        if (musicRef.current) {
          const newVolume = Math.max(
            0,
            Math.min(1, startVolume + volumeStep * currentStep),
          );
          musicRef.current.volume = newVolume;
        }

        if (currentStep >= steps) {
          if (fadeIntervalRef.current) {
            clearInterval(fadeIntervalRef.current);
            fadeIntervalRef.current = null;
          }
        }
      }, 50);
    },
    [],
  );

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const seekToTime = useCallback((timeMs: number) => {
    if (!musicRef.current) return;
    musicRef.current.currentTime = timeMs / 1000;
  }, []);

  const getCurrentTime = useCallback(() => {
    if (!musicRef.current) return 0;
    return musicRef.current.currentTime * 1000;
  }, []);

  return {
    isReady,
    isMusicPlaying,
    isMuted,
    playMusic,
    pauseMusic,
    resumeMusic,
    stopMusic,
    fadeMusic,
    toggleMute,
    seekToTime,
    getCurrentTime,
  };
}

export type { UseRewindMusicOptions, UseRewindMusicReturn };
